import {
  AdminLearningContentRow,
  AdminLearningContentSubCategory,
  AdminLearningContentTemplate,
} from '../types/domain';
import { PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY } from '../constants/adminLearningContentScope';
import { buildLearningContentScopeVersion } from './learningContentCacheVersion';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import {
  idbDeleteAdminLearningRowsBody,
  idbDeleteAdminLearningTemplatesBody,
  idbGetAdminLearningRowsBody,
  idbGetAdminLearningTemplatesBody,
  idbPutAdminLearningRowsBody,
  idbPutAdminLearningTemplatesBody,
} from './adminLearningContentIdb';

/**
 * v5: LS에는 contentVersion·headCheckedAt 메타만.
 * 본문(rows/templates)은 IndexedDB + 세션 메모리.
 * v4 LS 본문이 있으면 읽은 뒤 IDB로 이전하고 v4 키를 제거한다.
 */
const ROWS_META_VERSION = 'v5';
const TEMPLATES_META_VERSION = 'v5';
const ROWS_LEGACY_VERSION = 'v4';
const TEMPLATES_LEGACY_VERSION = 'v4';

/** head 재검증 TTL — 관리자 카탈로그는 드물게 갱신 */
export const ADMIN_LEARNING_CONTENT_HEAD_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type RowsMeta = {
  contentVersion: string;
  savedAt: number;
  headCheckedAt: number;
};

type TemplatesMeta = {
  contentVersion: string;
  savedAt: number;
  headCheckedAt: number;
};

const rowsMemory = new Map<string, AdminLearningContentRow[]>();
const templatesMemory = new Map<string, AdminLearningContentTemplate[]>();
const rowsMetaMemory = new Map<string, RowsMeta>();
const templatesMetaMemory = new Map<string, TemplatesMeta>();

function rowsBodyKey(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): string {
  const t = templateName.trim();
  return `${PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY}.${gradeNumber}.${subCategory}.${encodeURIComponent(t)}`;
}

function templatesBodyKey(gradeNumber: number, subCategory: AdminLearningContentSubCategory): string {
  return `${PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY}.${gradeNumber}.${subCategory}`;
}

function rowsMetaStorageKey(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): string {
  return `cp.adminLearningContent.rows.${ROWS_META_VERSION}.${rowsBodyKey(gradeNumber, subCategory, templateName)}`;
}

function templatesMetaStorageKey(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory
): string {
  return `cp.adminLearningContent.templates.${TEMPLATES_META_VERSION}.${templatesBodyKey(gradeNumber, subCategory)}`;
}

function rowsLegacyStorageKey(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): string {
  const t = templateName.trim();
  return `cp.adminLearningContent.rows.${ROWS_LEGACY_VERSION}.${PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY}.${gradeNumber}.${subCategory}.${encodeURIComponent(t)}`;
}

function templatesLegacyStorageKey(
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory
): string {
  return `cp.adminLearningContent.templates.${TEMPLATES_LEGACY_VERSION}.${PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY}.${gradeNumber}.${subCategory}`;
}

function normalizeRows(raw: unknown): AdminLearningContentRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is AdminLearningContentRow => !!row && typeof row === 'object');
}

function normalizeTemplates(raw: unknown): AdminLearningContentTemplate[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is AdminLearningContentTemplate => !!row && typeof row === 'object');
}

function readMetaFromLs(key: string): RowsMeta | TemplatesMeta | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RowsMeta> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    const contentVersion =
      typeof parsed.contentVersion === 'string' ? parsed.contentVersion.trim() : '';
    if (!contentVersion) return null;
    const savedAt = typeof parsed.savedAt === 'number' ? parsed.savedAt : 0;
    const headCheckedAt =
      typeof parsed.headCheckedAt === 'number' ? parsed.headCheckedAt : savedAt;
    return { contentVersion, savedAt, headCheckedAt };
  } catch {
    return null;
  }
}

function writeMetaToLs(key: string, meta: RowsMeta | TemplatesMeta): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(meta));
  } catch {
    /* quota */
  }
}

function removeLsKey(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function parseLegacyRowsPayload(raw: string): {
  rows: AdminLearningContentRow[];
  contentVersion: string;
  savedAt: number;
} | null {
  try {
    const parsed = JSON.parse(raw) as
      | { rows?: unknown; savedAt?: number; contentVersion?: string }
      | AdminLearningContentRow[];
    if (Array.isArray(parsed)) {
      const rows = normalizeRows(parsed);
      return {
        rows,
        contentVersion: buildLearningContentScopeVersion(rows),
        savedAt: Date.now(),
      };
    }
    if (!parsed || typeof parsed !== 'object') return null;
    const rows = normalizeRows(parsed.rows);
    const stored =
      typeof parsed.contentVersion === 'string' ? parsed.contentVersion.trim() : '';
    return {
      rows,
      contentVersion: stored || buildLearningContentScopeVersion(rows),
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

function parseLegacyTemplatesPayload(raw: string): {
  templates: AdminLearningContentTemplate[];
  contentVersion: string;
  savedAt: number;
} | null {
  try {
    const parsed = JSON.parse(raw) as
      | { templates?: unknown; savedAt?: number; contentVersion?: string }
      | AdminLearningContentTemplate[];
    if (Array.isArray(parsed)) {
      const templates = normalizeTemplates(parsed);
      return {
        templates,
        contentVersion: buildLearningContentScopeVersion(templates),
        savedAt: Date.now(),
      };
    }
    if (!parsed || typeof parsed !== 'object') return null;
    const templates = normalizeTemplates(parsed.templates);
    const stored =
      typeof parsed.contentVersion === 'string' ? parsed.contentVersion.trim() : '';
    return {
      templates,
      contentVersion: stored || buildLearningContentScopeVersion(templates),
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export function isAdminLearningContentHeadFresh(headCheckedAt: number | null | undefined): boolean {
  if (typeof headCheckedAt !== 'number' || !Number.isFinite(headCheckedAt) || headCheckedAt <= 0) {
    return false;
  }
  return Date.now() - headCheckedAt < ADMIN_LEARNING_CONTENT_HEAD_TTL_MS;
}

/** 동기: 세션 메모리(+레거시 LS 본문)만. IDB는 async 경로 사용. */
export function loadAdminLearningContentRowsCache(
  _workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): {
  found: boolean;
  rows: AdminLearningContentRow[];
  contentVersion: string | null;
  headCheckedAt: number | null;
} {
  if (!templateName.trim()) {
    return { found: false, rows: [], contentVersion: null, headCheckedAt: null };
  }
  const bodyKey = rowsBodyKey(gradeNumber, subCategory, templateName);
  const mem = rowsMemory.get(bodyKey);
  const metaMem = rowsMetaMemory.get(bodyKey);
  if (mem) {
    return {
      found: true,
      rows: mem,
      contentVersion: metaMem?.contentVersion ?? buildLearningContentScopeVersion(mem),
      headCheckedAt: metaMem?.headCheckedAt ?? null,
    };
  }

  if (typeof window === 'undefined') {
    return { found: false, rows: [], contentVersion: null, headCheckedAt: null };
  }

  // 레거시 v4 LS 본문 → 메모리 적재 (이어서 async 저장 시 IDB 이전)
  try {
    const legacyKey = rowsLegacyStorageKey(gradeNumber, subCategory, templateName);
    const raw = window.localStorage.getItem(legacyKey);
    if (raw) {
      const legacy = parseLegacyRowsPayload(raw);
      if (legacy) {
        rowsMemory.set(bodyKey, legacy.rows);
        const meta: RowsMeta = {
          contentVersion: legacy.contentVersion,
          savedAt: legacy.savedAt,
          headCheckedAt: legacy.savedAt,
        };
        rowsMetaMemory.set(bodyKey, meta);
        writeMetaToLs(rowsMetaStorageKey(gradeNumber, subCategory, templateName), meta);
        void idbPutAdminLearningRowsBody(bodyKey, legacy.rows).then(() => {
          removeLsKey(legacyKey);
        });
        return {
          found: true,
          rows: legacy.rows,
          contentVersion: legacy.contentVersion,
          headCheckedAt: meta.headCheckedAt,
        };
      }
    }
  } catch {
    /* ignore */
  }

  const meta = readMetaFromLs(rowsMetaStorageKey(gradeNumber, subCategory, templateName));
  if (meta) {
    rowsMetaMemory.set(bodyKey, meta);
  }
  return {
    found: false,
    rows: [],
    contentVersion: meta?.contentVersion ?? null,
    headCheckedAt: meta?.headCheckedAt ?? null,
  };
}

export async function loadAdminLearningContentRowsCacheAsync(
  workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): Promise<{
  found: boolean;
  rows: AdminLearningContentRow[];
  contentVersion: string | null;
  headCheckedAt: number | null;
}> {
  const sync = loadAdminLearningContentRowsCache(workspaceId, gradeNumber, subCategory, templateName);
  if (sync.found) return sync;
  if (!templateName.trim()) return sync;

  const bodyKey = rowsBodyKey(gradeNumber, subCategory, templateName);
  const fromIdb = await idbGetAdminLearningRowsBody<AdminLearningContentRow[]>(bodyKey);
  const rows = normalizeRows(fromIdb);
  if (!rows.length && !fromIdb) {
    return sync;
  }
  rowsMemory.set(bodyKey, rows);
  const metaKey = rowsMetaStorageKey(gradeNumber, subCategory, templateName);
  let meta = rowsMetaMemory.get(bodyKey) ?? readMetaFromLs(metaKey);
  if (!meta) {
    meta = {
      contentVersion: buildLearningContentScopeVersion(rows),
      savedAt: Date.now(),
      headCheckedAt: 0,
    };
    writeMetaToLs(metaKey, meta);
  }
  rowsMetaMemory.set(bodyKey, meta);
  return {
    found: true,
    rows,
    contentVersion: meta.contentVersion,
    headCheckedAt: meta.headCheckedAt,
  };
}

export function saveAdminLearningContentRowsCache(
  workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string,
  rows: AdminLearningContentRow[],
  options?: { force?: boolean; contentVersion?: string; headCheckedAt?: number }
): void {
  if (!templateName.trim() || typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY)) {
    return;
  }
  const bodyKey = rowsBodyKey(gradeNumber, subCategory, templateName);
  const contentVersion =
    (options?.contentVersion ?? '').trim() || buildLearningContentScopeVersion(rows);
  const now = Date.now();
  const meta: RowsMeta = {
    contentVersion,
    savedAt: now,
    headCheckedAt: typeof options?.headCheckedAt === 'number' ? options.headCheckedAt : now,
  };
  rowsMemory.set(bodyKey, rows);
  rowsMetaMemory.set(bodyKey, meta);
  writeMetaToLs(rowsMetaStorageKey(gradeNumber, subCategory, templateName), meta);
  void idbPutAdminLearningRowsBody(bodyKey, rows);
  removeLsKey(rowsLegacyStorageKey(gradeNumber, subCategory, templateName));
  void workspaceId;
}

export function touchAdminLearningContentRowsHeadChecked(
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string,
  contentVersion: string
): void {
  if (!templateName.trim()) return;
  const bodyKey = rowsBodyKey(gradeNumber, subCategory, templateName);
  const now = Date.now();
  const prev = rowsMetaMemory.get(bodyKey) ?? readMetaFromLs(rowsMetaStorageKey(gradeNumber, subCategory, templateName));
  const meta: RowsMeta = {
    contentVersion: contentVersion.trim() || prev?.contentVersion || '',
    savedAt: prev?.savedAt ?? now,
    headCheckedAt: now,
  };
  if (!meta.contentVersion) return;
  rowsMetaMemory.set(bodyKey, meta);
  writeMetaToLs(rowsMetaStorageKey(gradeNumber, subCategory, templateName), meta);
}

export function loadAdminLearningContentTemplatesCache(
  _workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory
): {
  found: boolean;
  templates: AdminLearningContentTemplate[];
  contentVersion: string | null;
  headCheckedAt: number | null;
} {
  const bodyKey = templatesBodyKey(gradeNumber, subCategory);
  const mem = templatesMemory.get(bodyKey);
  const metaMem = templatesMetaMemory.get(bodyKey);
  if (mem) {
    return {
      found: true,
      templates: mem,
      contentVersion: metaMem?.contentVersion ?? buildLearningContentScopeVersion(mem),
      headCheckedAt: metaMem?.headCheckedAt ?? null,
    };
  }

  if (typeof window === 'undefined') {
    return { found: false, templates: [], contentVersion: null, headCheckedAt: null };
  }

  try {
    const legacyKey = templatesLegacyStorageKey(gradeNumber, subCategory);
    const raw = window.localStorage.getItem(legacyKey);
    if (raw) {
      const legacy = parseLegacyTemplatesPayload(raw);
      if (legacy) {
        templatesMemory.set(bodyKey, legacy.templates);
        const meta: TemplatesMeta = {
          contentVersion: legacy.contentVersion,
          savedAt: legacy.savedAt,
          headCheckedAt: legacy.savedAt,
        };
        templatesMetaMemory.set(bodyKey, meta);
        writeMetaToLs(templatesMetaStorageKey(gradeNumber, subCategory), meta);
        void idbPutAdminLearningTemplatesBody(bodyKey, legacy.templates).then(() => {
          removeLsKey(legacyKey);
        });
        return {
          found: true,
          templates: legacy.templates,
          contentVersion: legacy.contentVersion,
          headCheckedAt: meta.headCheckedAt,
        };
      }
    }
  } catch {
    /* ignore */
  }

  const meta = readMetaFromLs(templatesMetaStorageKey(gradeNumber, subCategory));
  if (meta) {
    templatesMetaMemory.set(bodyKey, meta);
  }
  return {
    found: false,
    templates: [],
    contentVersion: meta?.contentVersion ?? null,
    headCheckedAt: meta?.headCheckedAt ?? null,
  };
}

export async function loadAdminLearningContentTemplatesCacheAsync(
  workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory
): Promise<{
  found: boolean;
  templates: AdminLearningContentTemplate[];
  contentVersion: string | null;
  headCheckedAt: number | null;
}> {
  const sync = loadAdminLearningContentTemplatesCache(workspaceId, gradeNumber, subCategory);
  if (sync.found) return sync;

  const bodyKey = templatesBodyKey(gradeNumber, subCategory);
  const fromIdb = await idbGetAdminLearningTemplatesBody<AdminLearningContentTemplate[]>(bodyKey);
  const templates = normalizeTemplates(fromIdb);
  if (!templates.length && !fromIdb) {
    return sync;
  }
  templatesMemory.set(bodyKey, templates);
  const metaKey = templatesMetaStorageKey(gradeNumber, subCategory);
  let meta = templatesMetaMemory.get(bodyKey) ?? readMetaFromLs(metaKey);
  if (!meta) {
    meta = {
      contentVersion: buildLearningContentScopeVersion(templates),
      savedAt: Date.now(),
      headCheckedAt: 0,
    };
    writeMetaToLs(metaKey, meta);
  }
  templatesMetaMemory.set(bodyKey, meta);
  return {
    found: true,
    templates,
    contentVersion: meta.contentVersion,
    headCheckedAt: meta.headCheckedAt,
  };
}

export function saveAdminLearningContentTemplatesCache(
  workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templates: AdminLearningContentTemplate[],
  options?: { force?: boolean; contentVersion?: string; headCheckedAt?: number }
): void {
  if (typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY)) {
    return;
  }
  const bodyKey = templatesBodyKey(gradeNumber, subCategory);
  const contentVersion =
    (options?.contentVersion ?? '').trim() || buildLearningContentScopeVersion(templates);
  const now = Date.now();
  const meta: TemplatesMeta = {
    contentVersion,
    savedAt: now,
    headCheckedAt: typeof options?.headCheckedAt === 'number' ? options.headCheckedAt : now,
  };
  templatesMemory.set(bodyKey, templates);
  templatesMetaMemory.set(bodyKey, meta);
  writeMetaToLs(templatesMetaStorageKey(gradeNumber, subCategory), meta);
  void idbPutAdminLearningTemplatesBody(bodyKey, templates);
  removeLsKey(templatesLegacyStorageKey(gradeNumber, subCategory));
  void workspaceId;
}

export function touchAdminLearningContentTemplatesHeadChecked(
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  contentVersion: string
): void {
  const bodyKey = templatesBodyKey(gradeNumber, subCategory);
  const now = Date.now();
  const prev =
    templatesMetaMemory.get(bodyKey) ?? readMetaFromLs(templatesMetaStorageKey(gradeNumber, subCategory));
  const meta: TemplatesMeta = {
    contentVersion: contentVersion.trim() || prev?.contentVersion || '',
    savedAt: prev?.savedAt ?? now,
    headCheckedAt: now,
  };
  if (!meta.contentVersion) return;
  templatesMetaMemory.set(bodyKey, meta);
  writeMetaToLs(templatesMetaStorageKey(gradeNumber, subCategory), meta);
}

export function clearAdminLearningContentRowsCache(
  _workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): void {
  if (!templateName.trim()) return;
  const bodyKey = rowsBodyKey(gradeNumber, subCategory, templateName);
  rowsMemory.delete(bodyKey);
  rowsMetaMemory.delete(bodyKey);
  removeLsKey(rowsMetaStorageKey(gradeNumber, subCategory, templateName));
  removeLsKey(rowsLegacyStorageKey(gradeNumber, subCategory, templateName));
  void idbDeleteAdminLearningRowsBody(bodyKey);
}

export async function renameAdminLearningContentRowsCache(
  workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  fromTemplateName: string,
  toTemplateName: string
): Promise<void> {
  const from = fromTemplateName.trim();
  const to = toTemplateName.trim();
  if (!from || !to || from === to) return;
  const cached = await loadAdminLearningContentRowsCacheAsync(
    workspaceId,
    gradeNumber,
    subCategory,
    from
  );
  if (!cached.found) {
    clearAdminLearningContentRowsCache(workspaceId, gradeNumber, subCategory, from);
    return;
  }
  const rows = cached.rows.map((row) => ({
    ...row,
    template_name: to,
  }));
  saveAdminLearningContentRowsCache(workspaceId, gradeNumber, subCategory, to, rows, {
    force: true,
    contentVersion: cached.contentVersion ?? undefined,
    headCheckedAt: cached.headCheckedAt ?? Date.now(),
  });
  clearAdminLearningContentRowsCache(workspaceId, gradeNumber, subCategory, from);
}

export async function patchAdminLearningContentTemplatesCache(
  workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  patch: (templates: AdminLearningContentTemplate[]) => AdminLearningContentTemplate[]
): Promise<void> {
  const cached = await loadAdminLearningContentTemplatesCacheAsync(
    workspaceId,
    gradeNumber,
    subCategory
  );
  const next = patch(cached.found ? cached.templates : []);
  saveAdminLearningContentTemplatesCache(workspaceId, gradeNumber, subCategory, next, {
    force: true,
  });
}

/** 세션 메모리만 비움(플랫폼 공용 IDB·LS 메타는 유지) */
export function clearAdminLearningContentMemoryCaches(): void {
  rowsMemory.clear();
  templatesMemory.clear();
  rowsMetaMemory.clear();
  templatesMetaMemory.clear();
}
